import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { initWebPush } from './pushClient';

import {
  Home,
  Users,
  Calendar,
  FileText,
  Clock,
  Settings,
  LogOut,
  CheckCircle,
  MessageCircle,
  Info,
  HelpCircle,
  RefreshCw,
  ClipboardList,
  ShieldCheck,
  FileSpreadsheet,
  X,
  Sparkles,
  Maximize2,
} from 'lucide-react';

import API from './api';
import MessageBell from './MessageBell';
import Messages from './Messages';

// --- ROLE / THEME MAP ---
const ROLE_THEME = {
  admin: {
    sidebarBg: 'bg-sky-900',
    sidebarBorder: 'border-sky-700',
    navActive: 'bg-sky-800 font-semibold',
    navHover: 'hover:bg-sky-700',
    sectionDivider: 'bg-sky-800',
  },
  teacher: {
    sidebarBg: 'bg-indigo-900',
    sidebarBorder: 'border-indigo-700',
    navActive: 'bg-indigo-800 font-semibold',
    navHover: 'hover:bg-indigo-700',
    sectionDivider: 'bg-indigo-800',
  },
  super_admin: {
    sidebarBg: 'bg-slate-900',
    sidebarBorder: 'border-slate-700',
    navActive: 'bg-slate-800 font-semibold',
    navHover: 'hover:bg-slate-700',
    sectionDivider: 'bg-slate-800',
  },
  parent: {
    sidebarBg: 'bg-emerald-900',
    sidebarBorder: 'border-emerald-700',
    navActive: 'bg-emerald-800 font-semibold',
    navHover: 'hover:bg-emerald-700',
    sectionDivider: 'bg-emerald-800',
  },
};

// --- NAV DEFINITIONS ---
const teacherNav = [
  { name: 'Dashboard', to: '/teacher', icon: <Home size={18} />, feature: 'teacher.dashboard', alwaysOn: true },
  { name: 'My Visits', to: '/teacher/visits', icon: <Calendar size={18} />, feature: 'teacher.my_visits' },
  { name: 'My Calendar', to: '/teacher/calendar', icon: <Calendar size={18} />, feature: 'teacher.calendar' },
  { name: 'My Day Planner', to: '/teacher/my-planner', icon: <Clock size={18} />, feature: 'teacher.day_planner' },
  { name: 'My Students', to: '/teacher/students-info', icon: <Users size={18} />, feature: 'teacher.my_students' },
  { name: 'My Students Skills', to: '/teacher/students', icon: <Users size={18} />, feature: 'teacher.my_students_skills' },
  { name: 'Substitute Skill Entries', to: '/teacher/substitute', icon: <Users size={18} />, feature: 'teacher.substitute_skills' },
  { name: 'Goals & Achievement', to: '/teacher/achievement', icon: <CheckCircle size={18} />, feature: 'teacher.goals_achievement' },
  { name: 'Instruments', to: '/teacher/instruments', icon: <ClipboardList size={18} />, feature: 'teacher.instruments' },
  { name: 'My Availability', to: '/teacher/availability', icon: <Calendar size={18} />, feature: 'teacher.availability' },
  { name: 'My Students Availability', to: '/teacher/students-availability', icon: <Calendar size={18} />, feature: 'teacher.students_availability' },
  { name: 'My Trainees', to: '/teacher/my-trainees', icon: <Clock size={18} />, feature: 'teacher.trainees' },
  { name: 'My Time Log', to: '/teacher/my-timelog', icon: <Clock size={18} />, feature: 'teacher.time_log' },
  { name: 'My Trip Log', to: '/teacher/my-triplog', icon: <Clock size={18} />, feature: 'teacher.trip_log' },
  { name: 'Messages', to: '/teacher/messages', icon: <MessageCircle size={18} />, feature: 'teacher.messages', alwaysOn: true },
  { name: 'Document Management', to: '/teacher/docs', icon: <FileText size={18} />, feature: 'teacher.docs' },
  { name: 'About & Help', to: '/teacher/about', icon: <Info size={18} />, feature: 'teacher.about', alwaysOn: true },
  { name: 'FAQ', to: '/teacher/faq', icon: <HelpCircle size={18} />, feature: 'teacher.faq', alwaysOn: true },
];

const adminNav = [
  { name: 'Dashboard', to: '/admin', icon: <Home size={18} />, feature: 'admin.dashboard', alwaysOn: true },

  { name: 'Visits', to: '/admin/visits', icon: <Calendar size={18} />, feature: 'admin.visits' },
  { name: 'Student Visits Scheduler', to: '/admin/scheduler', icon: <Calendar size={18} />, feature: 'admin.scheduler' },
  { name: 'Teacher Day Planner', to: '/admin/planer', icon: <Calendar size={18} />, feature: 'admin.teacher_day_planner' },
  { name: 'Visits Calendar', to: '/admin/calendar', icon: <Calendar size={18} />, feature: 'admin.calendar' },

  { name: 'Students', to: '/admin/students', icon: <Users size={18} />, feature: 'admin.students' },
  { name: 'Manage Students', to: '/admin/manage-students', icon: <Users size={18} />, feature: 'admin.manage_students' },
  { name: 'Students Availability', to: '/admin/students-availability', icon: <Calendar size={18} />, feature: 'admin.students_availability' },
  { name: 'Manage Teachers', to: '/admin/manage-teachers', icon: <Users size={18} />, feature: 'admin.manage_teachers' },
  { name: 'Teachers Availability', to: '/admin/availability', icon: <Calendar size={18} />, feature: 'admin.teachers_availability' },

  { name: 'Training Visits Planner', to: '/admin/training', icon: <Calendar size={18} />, feature: 'admin.training_visits_planner' },
  { name: 'Training Monitor', to: '/admin/mentor-training', icon: <Calendar size={18} />, feature: 'admin.mentor_training_dashboard' },

  { name: 'Teachers Work Report', to: '/admin/work-report', icon: <FileText size={18} />, feature: 'admin.work_report' },
  { name: 'Student Progress Report', to: '/admin/student-progress', icon: <FileText size={18} />, feature: 'admin.student_progress_report' },
  { name: 'Student Service Audit Report', to: '/admin/student-service-audit', icon: <ShieldCheck size={18} />, feature: 'admin.student_progress_report' },
  { name: 'Student Goals & Achievement', to: '/admin/achievement', icon: <CheckCircle size={18} />, feature: 'admin.goals_achievement' },
  { name: 'Teacher Time Logs', to: '/admin/teacher-timelogs', icon: <Clock size={18} />, feature: 'admin.teacher_timelogs' },
  { name: 'Teacher Trip Logs', to: '/admin/teacher-triplogs', icon: <Clock size={18} />, feature: 'admin.teacher_triplogs' },
  { name: 'AutoPilot & Trip Monitor', to: '/admin/autopilot-monitor', icon: <Users size={18} />, feature: 'admin.teacher_triplogs' },
  { name: 'Document Management', to: '/admin/docs', icon: <FileText size={18} />, feature: 'admin.docs' },

  { name: 'Instruments', to: '/admin/instruments', icon: <ClipboardList size={18} />, feature: 'admin.instruments' },
  { name: 'Intake Builder', to: '/admin/builder', icon: <ClipboardList size={18} />, feature: 'admin.builder' },

  { name: 'Insurance', to: '/admin/insurance', icon: <ShieldCheck size={18} />, feature: 'admin.insurance' },
  { name: 'Authorizations', to: '/admin/authorizations', icon: <ClipboardList size={18} />, feature: 'admin.authorizations' },
  { name: 'EVV Exceptions', to: '/admin/evv-exceptions', icon: <ShieldCheck size={18} />, feature: 'admin.evv_exceptions' },
  { name: 'Billing Export', to: '/admin/billing-export', icon: <FileSpreadsheet size={18} />, feature: 'admin.billing_export' },

  { name: 'Messages', to: '/admin/messages', icon: <MessageCircle size={18} />, feature: 'admin.messages', alwaysOn: true },
  { name: 'Settings', to: '/admin/settings', icon: <Settings size={18} />, feature: 'admin.settings', alwaysOn: true },
  { name: 'Active sessions', to: '/admin/sessions', icon: <HelpCircle size={18} />, feature: 'admin.sessions', alwaysOn: true },
  { name: 'KPIs', to: '/admin/kpis', icon: <Info size={18} />, feature: 'admin.kpis', alwaysOn: true },
  { name: 'Pricing', to: '/admin/pricing', icon: <Settings size={18} />, feature: 'admin.pricing', alwaysOn: true },

  { name: 'About & Help', to: '/admin/about', icon: <Info size={18} />, feature: 'admin.about', alwaysOn: true },
  { name: 'FAQ', to: '/admin/faq', icon: <HelpCircle size={18} />, feature: 'admin.faq', alwaysOn: true },
];

const superAdminNav = [
  { name: 'Overview', to: '/super', icon: <Home size={18} /> },
  { name: 'Organizations', to: '/super/organizations', icon: <Users size={18} /> },
  { name: 'Org Billing & Invoices', to: '/super/billing', icon: <FileText size={18} /> },
  { name: 'Org Disciplines', to: '/super/disciplines', icon: <Settings size={18} /> },
  { name: 'Instruments', to: '/super/instruments', icon: <ClipboardList size={18} /> },
  { name: 'Licenses', to: '/super/licenses', icon: <ShieldCheck size={18} /> },
  { name: 'Manage Taxonomy', to: '/super/taxonomy', icon: <Settings size={18} /> },
  { name: 'System Logs', to: '/super/audit-log', icon: <Clock size={18} /> },
  { name: 'Security Audit Logs', to: '/super/security-log', icon: <Clock size={18} /> },
  { name: 'Active sessions', to: '/super/sessions', icon: <Settings size={18} /> },
  { name: 'Messages', to: '/super/messages', icon: <MessageCircle size={18} /> },
  { name: 'Settings & Plans', to: '/super/settings', icon: <Settings size={18} /> },
  { name: 'KPIs', to: '/super/kpis', icon: <Settings size={18} /> },
  { name: 'AI Ops', to: '/super/aicache', icon: <Settings size={18} /> },
  { name: 'AI Entitlements', to: '/super/entitlements', icon: <Settings size={18} /> },
];

const parentNav = [
  { name: 'Dashboard', to: '/parent', icon: <Home size={18} />, feature: 'parent.dashboard', alwaysOn: true },
  { name: 'My Children', to: '/parent/students', icon: <Users size={18} />, feature: 'parent.children' },
  { name: 'Visits & Calendar', to: '/parent/visits', icon: <Calendar size={18} />, feature: 'parent.visits_calendar' },
  { name: 'Visits Scheduler', to: '/parent/scheduler', icon: <Calendar size={18} />, feature: 'parent.scheduler' },
  { name: 'Reports & Progress', to: '/parent/reports', icon: <FileText size={18} />, feature: 'parent.reports' },
  { name: 'Instruments', to: '/parent/instruments', icon: <ClipboardList size={18} />, feature: 'parent.instruments' },
  { name: 'My Availability', to: '/parent/availability', icon: <Calendar size={18} />, feature: 'parent.availability' },
  { name: 'Documents', to: '/parent/docs', icon: <FileText size={18} />, feature: 'parent.docs' },
  { name: 'Messages', to: '/parent/messages', icon: <MessageCircle size={18} />, feature: 'parent.messages', alwaysOn: true },
  { name: 'Profile & Settings', to: '/parent/profile', icon: <Settings size={18} />, feature: 'parent.profile_settings', alwaysOn: true },
];

// helper for route matching
const matchesPath = (itemTo, path) => itemTo === path || itemTo.startsWith(path + '/');

// --- CENTRAL HELP CONTENT ---
const HELP_CONTENT = {
  '/teacher': {
    purpose: 'Your main starting point for the day. Use this dashboard to review key tasks, today’s visits, and the fastest path into your daily workflow.',
    includes: [
      'a high-level view of today’s schedule and activity',
      'quick access to visits, planner, and messages',
      'training stage visibility and day-to-day work context',
      'a quick way to start GPS tracking using the small car icon when travel tracking is enabled',
    ],
    howTo: [
      'Open Dashboard at the start of the workday.',
      'Review today’s schedule, alerts, and unread messages.',
      'If GPS tracking is enabled for your workflow, click the small car icon to start trip tracking before travel.',
      'Continue into My Visits or My Day Planner to begin active work.',
      'After travel is recorded, use My Trip Log to review mileage and drive-time records that may be used for reimbursement or payment review.',
    ],
    tips: [
      'Use Dashboard as your launch point, then move into the module where the actual work happens.',
      'If your organization tracks drive time or mileage, start GPS tracking before travel so your trip data is available later in My Trip Log.',
    ],
    videos: [
      {
        title: 'How-to video',
        src: '/dashboard-teacher-ui.mp4',
      },
      {
        title: 'Accidental check-in',
        src: '/dashboard-accidental-check-in.mp4',
      },
    ],
  },
  '/teacher/visits': {
    purpose: 'Use this module to manage the visits assigned to you. It is the primary workspace for reviewing visit details and completing visit-related actions.',
    includes: [
      'scheduled visits assigned to the teacher',
      'visit status and core visit details',
      'check-in / check-out related workflow when enabled',
    ],
    howTo: [
      'Open My Visits before heading into the field.',
      'Confirm the visit time, student, service context, and location details.',
      'Open the visit to complete operational actions and documentation.',
    ],
    tips: ['This page is focused on visit execution, not long-range planning.'],
  },
  '/teacher/calendar': {
    purpose: 'Use Calendar for a broader view of your schedule across days and weeks.',
    includes: [
      'visit schedule laid out by date',
      'a visual view of workload and time blocks',
      'an easier way to spot schedule gaps or overlaps',
    ],
    howTo: [
      'Open Calendar when you need a wider weekly or monthly view.',
      'Select a visit or date to review details.',
      'Use My Day Planner when you need a more detailed daily timeline.',
    ],
    tips: ['Best for schedule visibility and planning context, not detailed execution.'],
  },
  '/teacher/my-planner': {
    purpose: 'This is your detailed daily timeline. Use it to understand how your day is structured, when you are booked, and where there is room to work.',
    includes: [
      'a time-based daily layout',
      'busy segments and working blocks',
      'the most operational view of your day',
    ],
    howTo: [
      'Open My Day Planner at the beginning of the day.',
      'Review the order of visits, work blocks, and open intervals.',
      'Use it throughout the day to stay aligned with real-time activity.',
    ],
    tips: ['This is the most operational daily view for field-based work.'],
  },
  '/teacher/students-info': {
    purpose: 'Use this page to review the core profile information for the students assigned to you.',
    includes: [
      'basic student profiles',
      'key identifying and operational information',
      'direct access points into related student workflows',
    ],
    howTo: [
      'Select a student from the list.',
      'Review the student profile and service context.',
      'Continue into skills, reports, visits, or other related modules as needed.',
    ],
    tips: ['Use this when you need a quick student overview without entering full admin management.'],
  },
  '/teacher/students': {
    purpose: 'This module supports skill tracking and progress updates for the student by the primary teacher.',
    includes: [
      'skill status by domain',
      'baseline setup and follow-up progression',
      'status history and notes',
    ],
    howTo: [
      'Choose a student first.',
      'Review domains and the current state of each skill.',
      'Update status and notes when progress is observed or needs to be recorded.',
    ],
    tips: ['This is one of the most important clinical progress modules in the system.'],
  },
  '/teacher/substitute': {
    purpose: 'Use this module when a substitute teacher documents student skills during a substitute visit.',
    includes: [
      'separate substitute skill entries',
      'clear separation from the primary teacher’s records',
      'traceability for substitute-based assessment work',
    ],
    howTo: [
      'Use this only when you are acting as the substitute provider for a visit.',
      'Select the student and relevant skill area.',
      'Record the observation as the substitute teacher.',
    ],
    tips: ['Important for audit history and clarity around who entered the update.'],
  },
  '/teacher/achievement': {
    purpose: 'Use this page to review and document student goals and achievements over time.',
    includes: [
      'goal statements and target outcomes',
      'achievement records',
      'narrative progress summaries',
    ],
    howTo: [
      'Open this module when reviewing goals for a reporting period.',
      'Add or review planned goals.',
      'Record achievements as progress is made.',
    ],
    tips: ['Works best when used alongside Student Skills and progress reporting workflows.'],
  },
  '/teacher/instruments': {
    purpose: 'This module is used for structured assessments, instruments, and forms assigned to the teacher.',
    includes: [
      'assigned forms and assessment instruments',
      'response entry and scoring workflow',
      'access to previously completed submissions',
    ],
    howTo: [
      'Open the correct instrument for the student or case.',
      'Complete the required items according to the assessment rules.',
      'Save or finalize the submission when complete.',
    ],
    tips: ['Use this when your organization has assessments or intake workflows enabled.'],
  },
  '/teacher/availability': {
    purpose: 'Set and manage your working availability so the system can schedule accurately.',
    includes: [
      'regular working days and hours',
      'exceptions, days off, and unavailable time',
      'the scheduling framework used by planning modules',
    ],
    howTo: [
      'Set your standard weekly availability first.',
      'Add exceptions when you are unavailable or your schedule changes.',
      'Review your availability regularly to keep it accurate.',
    ],
    tips: ['Accurate availability directly affects scheduling quality and reassignment logic.'],
  },
  '/teacher/students-availability': {
    purpose: 'Use this page to review when students are available for visits and services.',
    includes: [
      'student-specific available days and times',
      'exceptions and scheduling constraints',
      'planning support for accurate visit placement',
    ],
    howTo: [
      'Open a student or the broader availability list.',
      'Check which time ranges are available.',
      'Use that information when planning or reviewing visit timing.',
    ],
    tips: ['Especially useful when families have complex or changing scheduling constraints.'],
  },
  '/teacher/my-trainees': {
    purpose: 'Use this page if you are mentoring or supervising trainees within the organization.',
    includes: [
      'your trainee list',
      'their training stage and work status',
      'supporting context for mentorship and oversight',
    ],
    howTo: [
      'Open My Trainees to review assigned trainee users.',
      'Check their current stage and activity.',
      'Use together with training-related workflows when supervising progress.',
    ],
    tips: ['Only relevant if you serve as a mentor or supervisor.'],
  },
  '/teacher/my-timelog': {
    purpose: 'Use Time Log to review recorded working time tied to visits and other activity.',
    includes: [
      'worked time by visit and work block',
      'duration tracking',
      'reporting and payroll-related visibility',
    ],
    howTo: [
      'Open the log to review your recorded time.',
      'Compare entries against visits and scheduled work.',
      'Correct or follow up on discrepancies if your workflow allows it.',
    ],
    tips: ['Important for payroll, internal review, and audit readiness.'],
  },
  '/teacher/my-triplog': {
    purpose: 'Use Trip Log to review travel tied to field work and visits.',
    includes: [
      'trip records and route context',
      'distance and travel activity',
      'supporting data for cost and mileage analysis',
    ],
    howTo: [
      'Open Trip Log by day or date range.',
      'Compare trips against your assigned visits.',
      'Use it for review, reconciliation, or reimbursement support.',
    ],
    tips: ['Useful for organizations that track mileage or travel reimbursement.'],
  },
  '/teacher/messages': {
    purpose: 'This is the internal communication center for the system.',
    includes: [
      'channels, conversations, and unread status',
      'operational communication with team members',
      'a centralized communication record',
    ],
    howTo: [
      'Open Messages when you see an unread badge or need to send an update.',
      'Enter the correct conversation or channel.',
      'Reply within the relevant thread to keep communication organized.',
    ],
    tips: ['Keeps operational communication in one place instead of spreading it across external apps.'],
  },
  '/teacher/docs': {
    purpose: 'Use this module to review and manage documents related to your students, visits, and work.',
    includes: [
      'attached documents and files',
      'view and download access',
      'organized documentation for active cases and workflows',
    ],
    howTo: [
      'Open documents in the appropriate student, visit, or general context.',
      'Review, download, or add files if your permissions allow it.',
      'Keep documentation organized and current.',
    ],
    tips: ['Especially useful for onboarding, compliance, and ongoing case documentation.'],
  },
  '/teacher/about': {
    purpose: 'Use this page to understand the platform, its purpose, and the core workflows available to your role.',
    includes: [
      'system overview',
      'basic usage guidance',
      'general user orientation',
    ],
    howTo: [
      'Open About & Help when you need a quick orientation to the system.',
      'Use it as a starting point if you are new to the platform.',
    ],
    tips: ['A good first-stop page for onboarding new users.'],
  },
  '/teacher/faq': {
    purpose: 'Use FAQ for short answers to common day-to-day questions.',
    includes: [
      'common questions by workflow or module',
      'practical answers for routine situations',
      'quick self-service help',
    ],
    howTo: [
      'Search or browse for the question you have.',
      'Use FAQ first before escalating a support issue.',
    ],
    tips: ['Keep answers short, practical, and action-oriented.'],
  },

  '/admin': {
    purpose: 'This is the operational command center for the organization admin.',
    includes: [
      'a high-level view of core modules and status',
      'fast access to scheduling, user management, and analytics',
      'plan and subscription context for the organization',
    ],
    howTo: [
      'Open Dashboard as your admin home base.',
      'Review the organization’s current priorities and alerts.',
      'Move into the appropriate operational module from there.',
    ],
    tips: ['Think of this as the command center, not the place where every task is completed in detail.'],
  },
  '/admin/visits': {
    purpose: 'Use this page to monitor visits across the organization.',
    includes: [
      'filters by date, teacher, and status',
      'visit detail access',
      'a broad operational view of visit activity',
    ],
    howTo: [
      'Filter visits by timeframe, provider, or status.',
      'Open a visit row for more detailed information.',
      'Use it for day-to-day operational oversight.',
    ],
    tips: ['Best for monitoring the overall visit picture across the organization.'],
  },
  '/admin/scheduler': {
    purpose: 'Use this module to schedule student visits across a week and assign the best available provider.',
    includes: [
      'daily slot creation',
      'teacher suggestions based on availability, distance, and cost',
      'bulk save for weekly planning',
    ],
    howTo: [
      'Select the student first.',
      'Choose the target week and add time slots.',
      'Assign a teacher and save the schedule.',
    ],
    tips: ['This is one of the most important scheduling modules for scalable field operations.'],
  },
  '/admin/planer': {
    purpose: 'Use Teacher Day Planner to manage a provider’s day in detail.',
    includes: [
      'teacher-by-teacher timelines',
      'busy time, candidate slots, work blocks, and visits',
      'real-time operational schedule shaping',
    ],
    howTo: [
      'Choose the day and the teacher.',
      'Review free and busy segments.',
      'Add, adjust, or refine the schedule as needed.',
    ],
    tips: ['This is one of the most powerful tools for real-time operational scheduling.'],
  },
  '/admin/calendar': {
    purpose: 'Use Visits Calendar for a wider calendar view of operations across days and months.',
    includes: [
      'monthly calendar visibility',
      'day timeline drill-down',
      'filters and schedule-related stats',
    ],
    howTo: [
      'Open the month view to understand overall workload.',
      'Click into a day for more detail.',
      'Use filters to focus on the right segment of activity.',
    ],
    tips: ['Great for spotting gaps, clustering, and overall workload patterns.'],
  },
  '/admin/students': {
    purpose: 'Use this page for a broad operational view of students across the organization.',
    includes: [
      'core student profile access',
      'status and scheduling context',
      'entry points into related student workflows',
    ],
    howTo: [
      'Open the student list and apply filters if needed.',
      'Select a student to continue into a related workflow.',
    ],
    tips: ['Use this for operational visibility, not full record administration.'],
  },
  '/admin/manage-students': {
    purpose: 'This is the main administrative workspace for full student record management.',
    includes: [
      'create, edit, and update student records',
      'parent relationships, addresses, and operational fields',
      'import/export and supporting actions',
    ],
    howTo: [
      'Add a new student or open an existing record for editing.',
      'Review parent relationships, address details, and required fields.',
      'Save changes and verify the result in the main list.',
    ],
    tips: ['This is the primary master-data module for student records.'],
  },
  '/admin/students-availability': {
    purpose: 'Use this module to manage and review when students are available for services.',
    includes: [
      'weekly and date-specific availability windows',
      'exceptions and scheduling constraints',
      'planning support for scheduler and day planner tools',
    ],
    howTo: [
      'Select a student or filter the student group.',
      'Review available time windows and restrictions.',
      'Use the data to support accurate scheduling decisions.',
    ],
    tips: ['The quality of this data directly affects scheduling quality.'],
  },
  '/admin/manage-teachers': {
    purpose: 'This is the central admin workspace for provider management.',
    includes: [
      'active, inactive, and training-stage users',
      'rates, disciplines, and core provider details',
      'audit visibility and profile administration',
    ],
    howTo: [
      'Browse or filter the teacher list.',
      'Open a teacher profile to edit details.',
      'Save changes and confirm the result in the grid or list.',
    ],
    tips: ['One of the most important administrative modules in the system.'],
  },
  '/admin/availability': {
    purpose: 'Use this page to review and manage provider availability across the organization.',
    includes: [
      'work schedules, exceptions, and days off',
      'weekly and date-specific availability rules',
      'the core scheduling framework used by planning tools',
    ],
    howTo: [
      'Choose the provider.',
      'Review availability by week or specific date.',
      'Update the schedule whenever availability changes.',
    ],
    tips: ['Without accurate provider availability, scheduling quality breaks down quickly.'],
  },
  '/admin/training': {
    purpose: 'Use this page to plan training-related visits and teacher onboarding workflows.',
    includes: [
      'training visit planning',
      'mentor / trainee pairing context',
      'structured support for training operations',
    ],
    howTo: [
      'Define the training scenario first.',
      'Connect the mentor and trainee relationship.',
      'Plan visits and track how the training schedule is executed.',
    ],
    tips: ['Useful for organizations with a formal provider onboarding model.'],
  },
  '/admin/mentor-training': {
    purpose: 'Use this module to monitor mentors, trainees, and training stage progress.',
    includes: [
      'training stage visibility',
      'oversight of work and progress',
      'current mentor / trainee relationships',
    ],
    howTo: [
      'Open the monitor dashboard.',
      'Review trainee users and their assigned mentors.',
      'Use it for supervision, oversight, and admin decisions.',
    ],
    tips: ['Complements Training Visits Planner rather than replacing it.'],
  },
  '/admin/work-report': {
    purpose: 'Use this report to review provider work time, work blocks, and productivity-related activity.',
    includes: [
      'worked time and work block data',
      'reporting and operational analytics',
      'exportable datasets',
    ],
    howTo: [
      'Choose a timeframe and apply filters.',
      'Review results by provider or group.',
      'Export or use the results for internal analysis.',
    ],
    tips: ['Important for payroll, management review, and team efficiency analysis.'],
  },
  '/admin/student-service-audit': {
    purpose: 'Review a student service evidence report for a custom period.',
    includes: ['visits, clinical notes and skill history', 'G-sign, EVV and explicitly linked tracking evidence', 'source IDs, available changes and evidence limitations'],
    howTo: ['Select a student, period and evidence sections.', 'Generate and review the report, including missing-record warnings.', 'Print or export the selected evidence snapshot.'],
    tips: ['G-sign records do not independently authenticate a parent. Original records and administrative reviews are displayed separately.'],
  },
  '/admin/student-progress': {
    purpose: 'Use this module to create and review student progress reports.',
    includes: [
      'period-based reports',
      'goals and achievements narrative content',
      'a clinical summary of progress over time',
    ],
    howTo: [
      'Select the student and the reporting period.',
      'Generate or review the report.',
      'Use it for internal review and parent-facing communication.',
    ],
    tips: ['Closely tied to skills, goals, achievements, and reporting period logic.'],
  },
  '/admin/achievement': {
    purpose: 'Use this page to manage student goals and achievements from an administrative or reporting perspective.',
    includes: [
      'goal text and achievement records',
      'progress narrative support',
      'report-related outcome tracking',
    ],
    howTo: [
      'Choose the student and target period.',
      'Review or enter goals and achievements.',
      'Use this data when preparing formal reports.',
    ],
    tips: ['Works best when aligned with the progress report workflow.'],
  },
  '/admin/teacher-timelogs': {
    purpose: 'Use this page to review provider time logs across the organization.',
    includes: [
      'time data by provider and date range',
      'operational and payroll-related visibility',
      'support for reviewing inconsistencies',
    ],
    howTo: [
      'Filter by teacher and date range.',
      'Review durations, statuses, and exceptions.',
      'Compare results with visits and work records where needed.',
    ],
    tips: ['Useful for payroll validation and operational oversight.'],
  },
  '/admin/autopilot-monitor': {
    purpose: 'Review recorded teacher AutoPilot and GPS trip activity for this organization.',
    includes: ['last known teacher locations', 'Full and Assisted AutoPilot sessions', 'recorded daily routes and event history'],
    howTo: ['Select an organization date.', 'Select a teacher pin or list entry to inspect recorded day segments.', 'Check GPS capture time, server receipt time and review flags.'],
    tips: ['An active session or old marker does not confirm that a phone is online. This page is read-only.'],
  },
  '/admin/teacher-triplogs': {
    purpose: 'Use this module to review provider travel logs across the organization.',
    includes: [
      'distance, route, and travel records',
      'travel-related analytics',
      'supporting data for reimbursement or cost review',
    ],
    howTo: [
      'Filter by teacher and reporting period.',
      'Review route activity and total distance.',
      'Compare results with visits scheduled for the same day.',
    ],
    tips: ['Especially valuable for field-based organizations using travel cost logic.'],
  },
  '/admin/docs': {
    purpose: 'Use this module as the organization’s central document workspace.',
    includes: [
      'student, provider, and general documents',
      'upload, review, and organization workflow',
      'administrative control over documentation',
    ],
    howTo: [
      'Open the correct document context.',
      'Review existing files or upload new ones.',
      'Keep documentation structured and current.',
    ],
    tips: ['Strong document organization reduces administrative friction and confusion.'],
  },
  '/admin/instruments': {
    purpose: 'Use this module to oversee assessments, forms, and instrument workflows.',
    includes: [
      'active instruments',
      'submission flow and completion status',
      'entry into broader assessment workflows',
    ],
    howTo: [
      'Open the list of instruments.',
      'Select an instrument or submission to review.',
      'Track status, completion, and content from there.',
    ],
    tips: ['Closely related to intake builder and any licensing-based assessment workflow.'],
  },
  '/admin/builder': {
    purpose: 'Use Intake Builder to configure intake forms and structured assessment content.',
    includes: [
      'sections and item creation',
      'field type configuration',
      'versioning and activation workflow',
    ],
    howTo: [
      'Open a new or existing intake definition.',
      'Add sections, items, and field rules.',
      'Save and activate once the structure is ready.',
    ],
    tips: ['This is a configuration tool for admins, not a daily operational screen.'],
  },
  '/admin/insurance': {
    purpose: 'Use this module to manage and review insurance-related information tied to cases and services.',
    includes: [
      'insurance information by case or student',
      'operational review of key coverage details',
      'support for billing and compliance workflows',
    ],
    howTo: [
      'Open the relevant student or case.',
      'Review insurance details and status.',
      'Update information when coverage or payer details change.',
    ],
    tips: ['Important when the organization uses insurance-driven workflows.'],
  },
  '/admin/authorizations': {
    purpose: 'Use this page to manage service authorizations and approved service ranges.',
    includes: [
      'authorization periods and service limits',
      'authorization status tracking',
      'connections to scheduling and billing logic',
    ],
    howTo: [
      'Open the authorization for the relevant student or case.',
      'Review approved dates, service rules, and limits.',
      'Use the information to avoid scheduling or billing outside the approved scope.',
    ],
    tips: ['Critical for avoiding service delivery beyond approved authorization boundaries.'],
  },
  '/admin/evv-exceptions': {
    purpose: 'Use this page to review and resolve EVV-related exceptions and visit verification issues.',
    includes: [
      'unresolved EVV exceptions',
      'check-in / check-out discrepancy review',
      'compliance support for field-based visits',
    ],
    howTo: [
      'Open the module when an exception badge appears.',
      'Review the problematic visits.',
      'Resolve, document, or escalate the issue according to policy.',
    ],
    tips: ['A very important compliance screen for serious home-visit operations.'],
  },
  '/admin/billing-export': {
    purpose: 'Use this module to export billing-ready operational data.',
    includes: [
      'billing-ready datasets',
      'export controls by date and filter set',
      'support for downstream payer or accounting workflows',
    ],
    howTo: [
      'Choose the correct date range and filters.',
      'Review the result set.',
      'Export in the required format.',
    ],
    tips: ['Keep export logic stable and tightly controlled because downstream systems depend on it.'],
  },
  '/admin/messages': {
    purpose: 'This is the organization’s internal messaging center.',
    includes: [
      'conversations, channels, and unread indicators',
      'communication with teachers, parents, and staff',
      'a centralized communication record',
    ],
    howTo: [
      'Open Messages from the sidebar or the bell.',
      'Select the right conversation or channel.',
      'Reply within the appropriate thread to keep communication organized.',
    ],
    tips: ['Centralized communication improves clarity and auditability.'],
  },
  '/admin/settings': {
    purpose: 'Use Settings to manage organization-level system behavior and operational configuration.',
    includes: [
      'organization-level parameters',
      'working rules and feature settings',
      'core operational configuration',
    ],
    howTo: [
      'Review available settings carefully.',
      'Update only the options you clearly understand.',
      'Save changes and verify the downstream effect in related modules.',
    ],
    tips: ['Changes here can affect many parts of the platform.'],
  },
  '/admin/sessions': {
    purpose: 'Use this module to review active user sessions within the organization.',
    includes: [
      'active login sessions',
      'basic security and operational visibility',
      'support for access monitoring',
    ],
    howTo: [
      'Review active sessions.',
      'Identify old, suspicious, or unexpected sessions.',
      'Use it for admin monitoring and support situations.',
    ],
    tips: ['Useful for security review and account-related support.'],
  },
  '/admin/kpis': {
    purpose: 'Use KPIs to review key operational and management metrics for the organization.',
    includes: [
      'important performance indicators',
      'trend visibility and workload insight',
      'management-level analysis',
    ],
    howTo: [
      'Open the KPI dashboard.',
      'Filter by period, provider group, or operational segment.',
      'Use the results to support decisions and process improvements.',
    ],
    tips: ['This is a management layer, not a detailed task execution screen.'],
  },
  '/admin/pricing': {
    purpose: 'Use this page to review pricing and plan-related information tied to the organization’s product package.',
    includes: [
      'plan details and pricing context',
      'commercial package information',
      'subscription-related visibility',
    ],
    howTo: [
      'Open Pricing when reviewing product plan or package details.',
      'Compare current plan scope with available options.',
    ],
    tips: ['Helpful when evaluating product scope, plan level, or commercial fit.'],
  },
  '/admin/about': {
    purpose: 'Use this page for admin-focused help, platform orientation, and general system guidance.',
    includes: [
      'admin-oriented system overview',
      'module guidance and summary',
      'general help content',
    ],
    howTo: [
      'Open About & Help when you need orientation or a quick overview.',
    ],
    tips: ['A good place to keep role-specific onboarding guidance for admins.'],
  },
  '/admin/faq': {
    purpose: 'Use FAQ for quick answers to common admin questions.',
    includes: [
      'frequently asked operational questions',
      'practical answers for common admin tasks',
      'self-service support content',
    ],
    howTo: [
      'Search FAQ before opening a support request.',
    ],
    tips: ['Ideal for common questions such as where a workflow is handled or how a rule works.'],
  },

  '/super': {
    purpose: 'This is the global platform overview for the super admin role.',
    includes: [
      'cross-platform operational visibility',
      'entry points into organizations, billing, and system administration',
      'broad business and platform insight',
    ],
    howTo: [
      'Use Overview as your global starting point.',
      'Continue into organizations, billing, KPIs, or logs depending on the task.',
    ],
    tips: ['This is the platform-wide control layer above any single organization.'],
  },
  '/super/organizations': {
    purpose: 'Use this page to manage organizations across the entire platform.',
    includes: [
      'organization list and status',
      'core tenant information',
      'entry into deeper tenant-level controls',
    ],
    howTo: [
      'Browse organizations.',
      'Open the specific tenant you need to review.',
      'Check plan, status, and operational context.',
    ],
    tips: ['This is the central page for multi-tenant platform administration.'],
  },
  '/super/billing': {
    purpose: 'Use this page to monitor billing and invoicing across organizations.',
    includes: [
      'invoice visibility',
      'billing cycle and status review',
      'financial overview by tenant',
    ],
    howTo: [
      'Filter by organization or period.',
      'Review invoice and billing status details.',
    ],
    tips: ['Important for SaaS monetization and financial control.'],
  },
  '/super/disciplines': {
    purpose: 'Use this page to manage global discipline and service structure across the platform.',
    includes: [
      'discipline configuration',
      'connections to service type and program logic',
      'template structure for organizations',
    ],
    howTo: [
      'Review the global discipline setup.',
      'Update structure or assignments where needed.',
    ],
    tips: ['This is foundational to a multi-program platform architecture.'],
  },
  '/super/instruments': {
    purpose: 'Use this page to oversee platform-level instruments and assessment assets.',
    includes: [
      'instruments available to tenants',
      'global assessment configuration',
      'visibility into licensed or shared assessment structure',
    ],
    howTo: [
      'Review available instruments and their status at the platform level.',
    ],
    tips: ['Useful when multiple organizations depend on common assessment assets.'],
  },
  '/super/licenses': {
    purpose: 'Use this page to manage licenses tied to instruments, modules, or premium system capabilities.',
    includes: [
      'license status and scope',
      'activation and restriction tracking',
      'availability control by organization',
    ],
    howTo: [
      'Open the license or tenant context.',
      'Review what is active, restricted, or expiring.',
    ],
    tips: ['Especially important when monetizing premium modules or licensed instruments.'],
  },
  '/super/taxonomy': {
    purpose: 'Use this page to manage platform-wide taxonomy and structured classification rules.',
    includes: [
      'shared code sets and hierarchies',
      'platform-wide standardization',
      'data consistency controls',
    ],
    howTo: [
      'Review existing taxonomy carefully.',
      'Change only when you understand the broader system impact.',
    ],
    tips: ['Changes here may affect the entire platform.'],
  },
  '/super/audit-log': {
    purpose: 'Use Audit Log to review critical actions and system events across the platform.',
    includes: [
      'history of important actions',
      'system and user event traceability',
      'support and forensic review context',
    ],
    howTo: [
      'Filter by date, user, or entity.',
      'Review what happened, when it happened, and who triggered it.',
    ],
    tips: ['Essential for enterprise-grade oversight and support investigation.'],
  },
  '/super/sessions': {
    purpose: 'Use this module to review active sessions across the full platform.',
    includes: [
      'global login session visibility',
      'security and oversight context',
      'user activity monitoring',
    ],
    howTo: [
      'Review platform-wide sessions when investigating support or security concerns.',
    ],
    tips: ['Helpful for identifying unusual access patterns.'],
  },
  '/super/messages': {
    purpose: 'Use this page for global messaging oversight and platform-level communication visibility.',
    includes: [
      'message and channel visibility',
      'platform-level communication insight',
      'centralized messaging oversight',
    ],
    howTo: [
      'Open Messages when you need communication-related oversight or support visibility.',
    ],
    tips: ['Keep focus on support and operational visibility rather than micromanaging routine conversation flow.'],
  },
  '/super/settings': {
    purpose: 'Use this page for global platform settings and SaaS plan configuration.',
    includes: [
      'plan configuration',
      'system-wide settings',
      'platform-level operating rules',
    ],
    howTo: [
      'Review settings carefully before making changes.',
      'Confirm the downstream impact on tenants and modules.',
    ],
    tips: ['This is a high-impact administrative screen and should be managed carefully.'],
  },
  '/super/kpis': {
    purpose: 'Use this page to review platform-level KPIs, tenant trends, and business performance indicators.',
    includes: [
      'global operating metrics',
      'tenant growth and usage patterns',
      'management-level SaaS analytics',
    ],
    howTo: [
      'Open KPI dashboards.',
      'Review trends by tenant, timeframe, or product segment.',
    ],
    tips: ['Important for product strategy, tenant health, and SaaS growth decisions.'],
  },

  '/parent': {
    purpose: 'This is the parent dashboard and starting point for family-facing access.',
    includes: [
      'a simple overview of children, visits, and key updates',
      'quick links into visits, reports, and availability',
      'a more streamlined experience than admin or teacher workflows',
    ],
    howTo: [
      'Open Dashboard as your starting point.',
      'Continue into children, reports, or visit-related pages as needed.',
    ],
    tips: ['Parent workflows should stay simple, clear, and easy to follow.'],
  },
  '/parent/students': {
    purpose: 'Use this page to review the children connected to the parent account.',
    includes: [
      'child profile overview',
      'key service and status information',
      'entry points into related parent-facing workflows',
    ],
    howTo: [
      'Select the child you want to review.',
      'Open the child record to understand core information and current context.',
    ],
    tips: ['Keep this experience simple and easy to scan.'],
  },
  '/parent/visits': {
    purpose: 'Use this page to review scheduled visits and calendar information from the parent perspective.',
    includes: [
      'upcoming and past visits',
      'calendar-based scheduling visibility',
      'clear family-facing visit information',
    ],
    howTo: [
      'Review upcoming or recent visits.',
      'Open a visit for additional details if needed.',
    ],
    tips: ['Clarity matters here more than complexity.'],
  },
  '/parent/scheduler': {
    purpose: 'Use this page to review or participate in scheduling, depending on how the organization uses parent scheduling workflow.',
    includes: [
      'available time options or schedule requests',
      'interaction with scheduling workflow',
      'coordination between the family and service team',
    ],
    howTo: [
      'Open available time options.',
      'Select or propose a suitable time if the workflow allows it.',
    ],
    tips: ['Keep this workflow simple and easy for families to understand.'],
  },
  '/parent/reports': {
    purpose: 'Use this page to review student progress reports and family-facing updates.',
    includes: [
      'periodic reports',
      'goals and achievements',
      'a readable summary of progress over time',
    ],
    howTo: [
      'Choose the child and the reporting period.',
      'Open the report and review the progress summary.',
    ],
    tips: ['This page benefits from warm, clear, non-technical language.'],
  },
  '/parent/instruments': {
    purpose: 'Use this page for forms, questionnaires, and instruments that the parent needs to complete or review.',
    includes: [
      'intake forms and questionnaires',
      'submission status',
      'history of completed forms',
    ],
    howTo: [
      'Open the active form or instrument.',
      'Complete the required fields and save your responses.',
    ],
    tips: ['Simplicity is especially important in this workflow.'],
  },
  '/parent/availability': {
    purpose: 'Use this page to show when the family or child is available for visits and services.',
    includes: [
      'available days and times',
      'exceptions and schedule changes',
      'the foundation for better scheduling coordination',
    ],
    howTo: [
      'Enter or update available times.',
      'Save changes so scheduling reflects the current reality.',
    ],
    tips: ['Accurate availability reduces scheduling confusion.'],
  },
  '/parent/docs': {
    purpose: 'Use this page to review documents shared with the family.',
    includes: [
      'important files, forms, and documentation',
      'view and download access',
      'organized family-facing records',
    ],
    howTo: [
      'Open the document list.',
      'Review or download the file you need.',
    ],
    tips: ['Document titles and categories should be easy for families to understand.'],
  },
  '/parent/messages': {
    purpose: 'Use this page for secure communication with the organization.',
    includes: [
      'messages, conversations, and notifications',
      'a safer communication channel',
      'a record of communication inside the platform',
    ],
    howTo: [
      'Open Messages when you see an unread alert or need to send a message.',
      'Use the correct conversation or thread.',
    ],
    tips: ['Keep communication focused, clear, and easy to follow.'],
  },
  '/parent/profile': {
    purpose: 'Use this page to manage your profile and basic account settings.',
    includes: [
      'contact and account information',
      'basic profile settings',
      'self-service account review',
    ],
    howTo: [
      'Open Profile & Settings.',
      'Update your contact details or profile information as needed.',
    ],
    tips: ['This should feel like a simple self-service page.'],
  },
};

function getFallbackHelp(item, roleLabel) {
  return {
    purpose: `This module supports the "${item.name}" screen within the ${roleLabel} panel.`,
    includes: [
      'the data and views related to this module',
      'operational actions available to your role',
      'next-step workflow based on your enabled features and plan',
    ],
    howTo: [
      'Open the screen from the sidebar.',
      'Review the available sections and filters.',
      'Use it according to your role and the workflow enabled for your organization.',
    ],
    tips: ['You can refine this help text later with product-specific details as the module evolves.'],
  };
}

function getHelpForItem(item, roleLabel) {
  return HELP_CONTENT[item.to] || getFallbackHelp(item, roleLabel);
}

function HelpVideoCard({ title, src }) {
  const videoRef = useRef(null);
  const primedRef = useRef(false);

  const handleFullscreen = () => {
    const el = videoRef.current;
    if (!el) return;

    if (el.requestFullscreen) {
      el.requestFullscreen();
      return;
    }
    if (el.webkitRequestFullscreen) {
      el.webkitRequestFullscreen();
      return;
    }
    if (el.msRequestFullscreen) {
      el.msRequestFullscreen();
      return;
    }
    if (el.webkitEnterFullscreen) {
      el.webkitEnterFullscreen();
    }
  };

  const primeFirstVisibleFrame = () => {
    const el = videoRef.current;
    if (!el || primedRef.current) return;

    const seekToVisibleFrame = () => {
      try {
        if (el.duration && el.duration > 0.05 && el.currentTime === 0) {
          el.currentTime = 0.05;
          primedRef.current = true;
        }
      } catch (e) {
        // no-op
      }
    };

    if (el.readyState >= 2) {
      seekToVisibleFrame();
    } else {
      const onLoadedData = () => {
        seekToVisibleFrame();
        el.removeEventListener('loadeddata', onLoadedData);
      };
      el.addEventListener('loadeddata', onLoadedData);
    }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3">
      <div className="flex items-center justify-between gap-3 mb-2">
        <div className="text-sm font-semibold text-slate-900">{title}</div>
        <button
          type="button"
          onClick={handleFullscreen}
          className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          title="Open fullscreen"
          aria-label={`Open ${title} fullscreen`}
        >
          <Maximize2 size={14} />
          <span>Fullscreen</span>
        </button>
      </div>

      <video
        ref={videoRef}
        className="w-full rounded-lg border border-slate-200 bg-slate-100 max-h-52 object-cover"
        controls
        preload="auto"
        playsInline
        
        onLoadedMetadata={primeFirstVisibleFrame}
        onCanPlay={primeFirstVisibleFrame}
      >
        <source src={src} type="video/mp4" />
        Your browser does not support the video tag.
      </video>
    </div>
  );
}

function SidebarHelpDrawer({
  open,
  onClose,
  item,
  roleLabel,
  enabled,
  trainingBlocked,
  orgGuard,
}) {
  if (!open || !item) return null;

  const help = getHelpForItem(item, roleLabel);

  let availabilityText = 'Available in your current access scope.';
  if (!enabled) {
    if (trainingBlocked) {
      availabilityText = 'This module is currently unavailable because of your training stage restrictions.';
    } else if (orgGuard?.orgStatus === 'suspended') {
      availabilityText = 'This module is read-only or unavailable because the organization is inactive or suspended.';
    } else {
      availabilityText = 'This module is not included in your current plan or feature package.';
    }
  }

  return (
    <div className="fixed inset-0 z-[70]">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-2xl border-l border-slate-200 flex flex-col">
        <div className="px-4 py-3 border-b border-slate-200 flex items-start justify-between gap-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">
              Quick help
            </div>
            <h3 className="text-base font-semibold text-slate-900">
              {item.name}
            </h3>
            <div className="mt-1 flex flex-wrap gap-2">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-slate-100 text-slate-700 border border-slate-200">
                {roleLabel}
              </span>
              <span
                className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] border ${
                  enabled
                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                    : 'bg-amber-50 text-amber-700 border-amber-200'
                }`}
              >
                {enabled ? 'Available' : 'Restricted'}
              </span>
              {item.feature && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-sky-50 text-sky-700 border border-sky-200">
                  {item.feature}
                </span>
              )}
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="inline-flex items-center justify-center rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-800"
            aria-label="Close help"
            title="Close help"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5">
          {help.videos?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Quick videos
              </h4>
              <div className="space-y-3">
                {help.videos.map((video, idx) => (
                  <HelpVideoCard
                    key={`${video.src}-${idx}`}
                    title={video.title}
                    src={video.src}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-semibold text-slate-800 mb-1">
              What this page is for
            </div>
            <p className="text-sm text-slate-700 leading-6">{help.purpose}</p>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">
              What you will typically find here
            </h4>
            <ul className="space-y-2">
              {(help.includes || []).map((row, idx) => (
                <li key={idx} className="flex gap-2 text-sm text-slate-700">
                  <span className="mt-[7px] h-1.5 w-1.5 rounded-full bg-sky-500 shrink-0" />
                  <span>{row}</span>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="text-sm font-semibold text-slate-900 mb-2">
              How to use it
            </h4>
            <ol className="space-y-2">
              {(help.howTo || []).map((row, idx) => (
                <li key={idx} className="flex gap-3 text-sm text-slate-700">
                  <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-200 text-[11px] font-semibold text-slate-700 shrink-0">
                    {idx + 1}
                  </span>
                  <span>{row}</span>
                </li>
              ))}
            </ol>
          </div>

          {help.tips?.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold text-slate-900 mb-2">
                Pro tip
              </h4>
              <div className="space-y-2">
                {help.tips.map((tip, idx) => (
                  <div
                    key={idx}
                    className="rounded-lg border border-indigo-100 bg-indigo-50 px-3 py-2 text-sm text-indigo-800"
                  >
                    {tip}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <div className="text-xs font-semibold text-amber-900 mb-1">
              Availability in your account
            </div>
            <p className="text-sm text-amber-800 leading-6">{availabilityText}</p>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-3">
            <div className="text-xs font-semibold text-slate-800 mb-1">
              Why this helps in a professional SaaS
            </div>
            <p className="text-sm text-slate-700 leading-6">
              Contextual help reduces onboarding friction, shortens training time,
              lowers support volume, and helps users understand complex workflows
              without leaving the page they are already working on.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

const BaseLayout = ({ role: propRole = 'teacher', children }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const [open, setOpen] = useState(false);
  const [user, setUser] = useState(
    () => JSON.parse(localStorage.getItem('user') || '{}')
  );

  const [impersonator, setImpersonator] = useState(() => {
    try {
      const raw = localStorage.getItem('impersonator_user');
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  });

  const [unreadCount, setUnreadCount] = useState(0);
  const [showMessagesModal, setShowMessagesModal] = useState(false);

  // EVV exceptions badge count
  const [evvExceptionsCount, setEvvExceptionsCount] = useState(0);

  // AI Assistant badge count (teacher only)
  const [aiAssistantCount, setAiAssistantCount] = useState(0);

  // contextual sidebar help
  const [helpState, setHelpState] = useState({
    open: false,
    item: null,
    enabled: true,
    trainingBlocked: false,
  });

  // feature flags and org status
  const [featureFlags, setFeatureFlags] = useState(null);
  const [orgGuard, setOrgGuard] = useState({
    orgStatus: 'unknown',
    billing_plan: null,
    subscription_status: null,
    is_active: 1,
  });

  const displayName = user.name ? user.name : '';
  const role = user.role || propRole;

  const trainingStage =
    role === 'teacher'
      ? String(user.training_stage || 'none').trim().toLowerCase()
      : 'none';

  const navItems =
    role === 'admin'
      ? adminNav
      : role === 'super_admin'
      ? superAdminNav
      : role === 'parent'
      ? parentNav
      : teacherNav;

  const baseTheme = ROLE_THEME[role] || ROLE_THEME.teacher;
  const theme = baseTheme;

  const homePath =
    role === 'admin'
      ? '/admin'
      : role === 'super_admin'
      ? '/super'
      : role === 'parent'
      ? '/parent'
      : '/teacher';

  const handleLogout = async () => {
    try {
      await API.post('/logout');
    } catch (err) {
      console.error('Logout API error:', err);
    } finally {
      localStorage.removeItem('impersonator_token');
      localStorage.removeItem('impersonator_user');
      localStorage.clear();
      setUser({});
      setImpersonator(null);
      navigate('/');
    }
  };

  useEffect(() => {
    setUser(JSON.parse(localStorage.getItem('user') || '{}'));
    try {
      const rawImp = localStorage.getItem('impersonator_user');
      setImpersonator(rawImp ? JSON.parse(rawImp) : null);
    } catch (e) {
      setImpersonator(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    const fetchFeatures = async () => {
      try {
        const token = localStorage.getItem('token');
        const rawUser = localStorage.getItem('user');
        if (!token || !rawUser) return;

        const u = JSON.parse(rawUser || '{}');
        const r = u.role || propRole;
        if (!u.id || r === 'super_admin') return;

        const res = await API.get('/me/features');
        const data = res.data || {};
        const flags = {};
        (data.features || []).forEach((key) => {
          flags[key] = true;
        });

        setFeatureFlags(flags);
        setOrgGuard({
          orgStatus: data.orgStatus || 'ok',
          billing_plan: data.org?.billing_plan || null,
          subscription_status: data.org?.subscription_status || null,
          is_active: data.org?.is_active ?? 1,
        });
      } catch (e) {
        console.error('Failed to load feature flags', e);
        setFeatureFlags(null);
        setOrgGuard((prev) => ({ ...prev, orgStatus: 'unknown' }));
      }
    };

    fetchFeatures();
  }, [location.pathname, propRole]);

  const messagesFeatureKey =
    role === 'admin'
      ? 'admin.messages'
      : role === 'parent'
      ? 'parent.messages'
      : role === 'teacher'
      ? 'teacher.messages'
      : null;

  const canUseMessages =
    role === 'super_admin'
      ? true
      : !featureFlags
      ? true
      : messagesFeatureKey
      ? !!featureFlags[messagesFeatureKey]
      : false;

  const evvExceptionsFeatureKey = role === 'admin' ? 'admin.evv_exceptions' : null;

  const canUseEvvExceptions =
    role !== 'admin'
      ? false
      : !featureFlags
      ? true
      : evvExceptionsFeatureKey
      ? !!featureFlags[evvExceptionsFeatureKey]
      : false;

  const refreshUnread = useCallback(async () => {
    try {
      if (!canUseMessages) {
        setUnreadCount(0);
        return;
      }

      const res = await API.get('/messages/channels');
      const chans = res.data || [];

      let count = 0;
      chans.forEach((ch) => {
        const lastMsg = ch.last_message_at;
        const lastRead = ch.last_read_at;
        if (lastMsg && (!lastRead || lastMsg > lastRead)) {
          count += 1;
        }
      });

      setUnreadCount(count);
    } catch (e) {
      console.error('Failed to fetch unread messages', e);
    }
  }, [canUseMessages]);

  const refreshEvvExceptions = useCallback(async () => {
    try {
      if (!canUseEvvExceptions) {
        setEvvExceptionsCount(0);
        return;
      }
      const res = await API.get('/evv-exceptions/unresolved-count');
      const c = Number(res.data?.count || 0);
      setEvvExceptionsCount(c);
    } catch (e) {
      console.error('Failed to fetch EVV exceptions count', e);
    }
  }, [canUseEvvExceptions]);

  const refreshAiAssistant = useCallback(async () => {
    try {
      if (role !== 'teacher') {
        setAiAssistantCount(0);
        return;
      }
      const res = await API.get('/teacher/ai-assistant-summary');
      const data = res.data || {};
      const total =
        Number(data.missing_goals_count || 0) +
        Number(data.missing_achievements_count || 0);
      setAiAssistantCount(total);
    } catch (e) {
      console.error('Failed to fetch AI assistant summary', e);
      setAiAssistantCount(0);
    }
  }, [role]);

  useEffect(() => {
    refreshUnread();
    refreshEvvExceptions();
    refreshAiAssistant();

    const interval = setInterval(() => {
      refreshUnread();
      refreshEvvExceptions();
      refreshAiAssistant();
    }, 10000);

    return () => clearInterval(interval);
  }, [refreshUnread, refreshEvvExceptions, refreshAiAssistant]);

  const toggleMessagesModal = () => {
    if (!canUseMessages) return;
    setShowMessagesModal((prev) => !prev);
  };

  const getSectionLabel = (index) => {
    if (role === 'teacher') {
      if (index === 0) return 'Daily work';
      if (index === 9) return 'Availability & planning';
      if (index === 14) return 'Communication & documents';
      if (index === 16) return 'Help & info';
    } else if (role === 'admin') {
      if (index === 0) return 'Operations';
      if (index === 5) return 'Students & teachers';
      if (index === 10) return 'Training teachers';
      if (index === 12) return 'Reports & analytics';
      if (index === 20) return 'Assessments & intake';
      if (index === 22) return 'Billing & compliance';
      if (index === 26) return 'Communication & settings';
      if (index === 31) return 'Help & info';
    }
    return null;
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    if (token && u && u.id) {
      initWebPush();
    }
  }, []);

  const roleLabel =
    role === 'admin'
      ? 'Admin'
      : role === 'super_admin'
      ? 'Super Admin'
      : role === 'parent'
      ? 'Parent'
      : 'Teacher';

  const handleBackToSuper = () => {
    const impToken = localStorage.getItem('impersonator_token');
    const impUserRaw = localStorage.getItem('impersonator_user');

    if (impToken && impUserRaw) {
      localStorage.setItem('token', impToken);
      localStorage.setItem('user', impUserRaw);

      localStorage.removeItem('impersonator_token');
      localStorage.removeItem('impersonator_user');
      setImpersonator(null);

      setUser(JSON.parse(impUserRaw));
      navigate('/super');
    } else {
      localStorage.clear();
      setUser({});
      setImpersonator(null);
      navigate('/');
    }
  };

  const isNavAllowedForTrainingStage = (item) => {
    if (role !== 'teacher') return true;

    const stage = trainingStage || 'none';

    if (stage === 'shadow') {
      const blockedForShadow = [
        '/teacher/calendar',
        '/teacher/students-info',
        '/teacher/students',
        '/teacher/substitute',
        '/teacher/achievement',
        '/teacher/instruments',
        '/teacher/builder',
        '/teacher/students-availability',
        '/teacher/my-planner',
        '/teacher/my-timelog',
        '/teacher/my-triplog',
        '/teacher/my-trainees',
        '/teacher/docs',
      ];
      return !blockedForShadow.some((path) => matchesPath(item.to, path));
    }

    if (stage === 'supervised') {
      const blockedForSupervised = [
        '/teacher/calendar',
        '/teacher/students',
        '/teacher/substitute',
        '/teacher/achievement',
        '/teacher/instruments',
        '/teacher/builder',
        '/teacher/students-availability',
        '/teacher/my-planner',
        '/teacher/my-timelog',
        '/teacher/my-triplog',
        '/teacher/my-trainees',
        '/teacher/docs',
      ];
      return !blockedForSupervised.some((path) => matchesPath(item.to, path));
    }

    return true;
  };

  const isNavItemEnabled = (item) => {
    if (!isNavAllowedForTrainingStage(item)) {
      return false;
    }

    if (role === 'super_admin') return true;

    if (!featureFlags) return true;

    if (orgGuard.orgStatus === 'suspended' && !item.alwaysOn) {
      return false;
    }

    if (!item.feature) return true;

    return !!featureFlags[item.feature];
  };

  const openHelpForItem = (item, enabled, trainingBlocked) => {
    setHelpState({
      open: true,
      item,
      enabled,
      trainingBlocked,
    });
  };

  const closeHelp = () => {
    setHelpState({
      open: false,
      item: null,
      enabled: true,
      trainingBlocked: false,
    });
  };

  return (
    <div className="flex h-screen overflow-hidden">
      {open && (
        <div
          className="fixed inset-0 z-30 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed z-40 inset-y-0 left-0 w-64 text-white transform ${
          open ? 'translate-x-0' : '-translate-x-full'
        } transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0 lg:flex ${theme.sidebarBg}`}
      >
        <div className="flex flex-col w-64 h-full overflow-y-auto">
          {/* HEADER */}
          <div className={`flex items-center justify-between p-3 border-b ${theme.sidebarBorder}`}>
            <Link to={homePath} className="flex flex-col">
              <span className="text-sm font-semibold tracking-wide">
                VisitOMS EarlyCare
              </span>
              <span className="text-[11px] text-sky-100">
                {roleLabel} Dashboard
              </span>
            </Link>
            <button className="lg:hidden" onClick={() => setOpen(false)}>
              ✕
            </button>
          </div>

          {/* NAV ITEMS */}
          <nav className="flex-1 p-4 space-y-2">
            {navItems.map((item, index) => {
              const isActive = location.pathname === item.to;
              const isMessagesLink = item.to.endsWith('/messages');
              const isEvvExceptionsLink = item.to.endsWith('/evv-exceptions');
              const sectionLabel = getSectionLabel(index);

              const enabled = isNavItemEnabled(item);
              const trainingBlocked =
                role === 'teacher' &&
                trainingStage !== 'none' &&
                !isNavAllowedForTrainingStage(item);

              const baseClasses = `flex items-center gap-2 px-3 py-2 rounded transition ${
                isActive ? theme.navActive : theme.navHover
              }`;

              const rightSide = (
                <div className="flex items-center gap-2">
                  {isMessagesLink && unreadCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-purple-500 text-white">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}

                  {isEvvExceptionsLink && evvExceptionsCount > 0 && (
                    <span className="ml-2 px-2 py-0.5 rounded-full text-[10px] bg-rose-500 text-white">
                      {evvExceptionsCount > 9 ? '9+' : evvExceptionsCount}
                    </span>
                  )}

                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      openHelpForItem(item, enabled, trainingBlocked);
                    }}
                    className="inline-flex items-center justify-center rounded-full p-1 text-slate-300 hover:text-white hover:bg-white/10 transition"
                    title={`About ${item.name}`}
                    aria-label={`About ${item.name}`}
                  >
                    <span className="text-xs italic font-serif leading-none">i</span>
                  </button>
                </div>
              );

              return (
                <React.Fragment key={item.name}>
                  {sectionLabel && (
                    <div className="mt-1 mb-1">
                      <div className="text-[10px] uppercase tracking-wide text-sky-100 opacity-70">
                        {sectionLabel}
                      </div>
                      <div className={`h-px opacity-60 mt-1 ${theme.sectionDivider}`} />
                    </div>
                  )}

                  {enabled ? (
                    <Link to={item.to} className={baseClasses}>
                      {item.icon}
                      <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                        <span className="text-sm leading-5 pr-1 line-clamp-2">
                          {item.name}
                        </span>
                        {rightSide}
                      </div>
                    </Link>
                  ) : (
                    <div
                      className={`${baseClasses} opacity-40 cursor-not-allowed`}
                      title={
                        trainingBlocked
                          ? 'Not available in your current training stage.'
                          : orgGuard.orgStatus === 'suspended'
                          ? 'Organization inactive – please contact support to reactivate.'
                          : 'Not available in your current plan.'
                      }
                    >
                      {item.icon}
                      <div className="flex-1 min-w-0 flex items-start justify-between gap-2">
                        <span className="text-sm leading-5 pr-1 line-clamp-2">
                          {item.name}
                        </span>
                        {rightSide}
                      </div>
                    </div>
                  )}
                </React.Fragment>
              );
            })}

            {/* LOGOUT */}
            <button
              onClick={handleLogout}
              className={`mt-4 flex items-center gap-2 px-3 py-2 rounded transition w-full text-left ${theme.navHover}`}
            >
              <LogOut size={18} /> Logout
            </button>
          </nav>
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-y-auto h-screen">
        {/* Header */}
        <div className="bg-white shadow px-4 py-3 flex items-center justify-between sticky top-0 z-20">
          <div className="flex flex-col gap-1">
            <div className="flex items-center flex-wrap gap-2">
              <h2 className="font-semibold text-lg">
                {displayName
                  ? `${displayName} - ${roleLabel}`
                  : `${roleLabel} Panel`}
                <span className="ml-2 text-xs text-gray-500">
                  VisitOMS EarlyCare v2.76
                </span>
                {role === 'teacher' && trainingStage !== 'none' && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 border border-amber-300">
                    Training:{' '}
                    {trainingStage === 'shadow'
                      ? 'Shadow'
                      : trainingStage === 'supervised'
                      ? 'Supervised'
                      : trainingStage}
                  </span>
                )}
              </h2>

              {role === 'admin' && orgGuard.billing_plan && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-sky-100 text-sky-800 border border-sky-200">
                  Plan: {orgGuard.billing_plan}
                  {orgGuard.subscription_status && (
                    <span className="ml-1 text-[10px] text-sky-700">
                      ({orgGuard.subscription_status})
                    </span>
                  )}
                </span>
              )}

              {role === 'teacher' && aiAssistantCount > 0 && (
                <Link
                  to="/teacher"
                  className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-[11px] bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-100"
                  title="VisitOMS AI Assistant reminder"
                >
                  <Sparkles size={13} />
                  <span>AI Assistant</span>
                  <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-indigo-600 text-white text-[10px] px-1">
                    {aiAssistantCount > 99 ? '99+' : aiAssistantCount}
                  </span>
                </Link>
              )}

              {role !== 'super_admin' && orgGuard.orgStatus === 'suspended' && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] bg-red-100 text-red-700 border border-red-300">
                  Organization inactive – read-only mode
                </span>
              )}
            </div>

            {impersonator && (
              <button
                type="button"
                onClick={handleBackToSuper}
                className="inline-flex items-center max-w-full px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-800 border border-amber-300"
              >
                <span className="truncate">
                  Impersonating this user – back to{' '}
                  <strong>{impersonator.name || 'Super Admin'}</strong>
                </span>
              </button>
            )}
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => window.location.reload()}
                className="inline-flex items-center gap-2 px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 text-slate-700"
                title="Reload page"
                aria-label="Reload page"
              >
                <RefreshCw size={16} />
                <span className="hidden sm:inline text-xs">Reload</span>
              </button>
            </div>

            {canUseMessages && (
              <MessageBell role={role} count={unreadCount} onClick={toggleMessagesModal} />
            )}
            <button
              className="lg:hidden text-2xl"
              onClick={() => setOpen(!open)}
            >
              ☰
            </button>
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 overflow-y-auto p-4">{children}</main>
      </div>

      {/* Messages modal */}
      {showMessagesModal && canUseMessages && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-40 flex items-center justify-center">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-5xl h-[80vh] max-h-[90vh] flex flex-col">
            <div className="flex items-center justify-between px-4 py-2 border-b">
              <h3 className="font-semibold text-sm">Messages</h3>
              <button
                className="text-gray-500 hover:text-gray-800 text-lg"
                onClick={toggleMessagesModal}
              >
                ✕
              </button>
            </div>

            <div className="flex-1 min-h-0 p-3">
              <Messages
                mode={
                  role === 'admin'
                    ? 'admin'
                    : role === 'super_admin'
                    ? 'super'
                    : role === 'parent'
                    ? 'parent'
                    : 'teacher'
                }
                onRefreshUnread={refreshUnread}
                currentUserId={user?.id}
              />
            </div>
          </div>
        </div>
      )}

      {/* Sidebar Help Drawer */}
      <SidebarHelpDrawer
        open={helpState.open}
        onClose={closeHelp}
        item={helpState.item}
        roleLabel={roleLabel}
        enabled={helpState.enabled}
        trainingBlocked={helpState.trainingBlocked}
        orgGuard={orgGuard}
      />
    </div>
  );
};

export default BaseLayout;


